import { useState, useEffect } from 'react';
import { Stack } from '@mui/material';
import {
  EasyCheckDialog,
  EasyCheckTextField,
  EasyCheckSelect,
  EasyCheckButton
} from '../../common/Controls';

import { useForm, Controller } from 'react-hook-form';

const currencies = [
  { value: 'AED', label: 'UAE Dirham (د.إ)' },
  { value: 'OMR', label: 'Omani Rial (ر.ع.)' },
  { value: 'SAR', label: 'Saudi Riyal (ر.س)' },
  { value: 'BHD', label: 'Bahraini Dinar (د.ب)' },
  { value: 'KWD', label: 'Kuwaiti Dinar (د.ك)' },
  { value: 'QAR', label: 'Qatari Riyal (ر.ق)' },
  { value: 'EGP', label: 'Egyptian Pound (ج.م)' },
  { value: 'EUR', label: 'Euro (€)' },
];

const BankFormModal = ({ open, onClose, onSave, bank }) => {
  const [loading, setLoading] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm({
    defaultValues: {
      accountNumber: '',
      accountName: '',
      currency: 'AED'
    }
  });

  useEffect(() => {
    if (bank) {
      reset({
        accountNumber: bank.accountNumber || '',
        accountName: bank.accountName || '',
        currency: bank.currency || 'AED'
      });
    } else {
      reset({
        accountNumber: '',
        accountName: '',
        currency: 'AED'
      });
    }
  }, [bank, reset]);

  const onSubmit = async (data) => {
    console.log('📝 Modal form submission started with data:', data);
    setLoading(true);
    try {
      console.log('📝 Calling onSave function...');
      await onSave(data);
      console.log('📝 onSave completed successfully');
      reset();
      setLoading(false);
    } catch (error) {
      console.error('📝 Error in form submission:', error);
      setLoading(false);
    }
  };

  const handleClose = () => {
    console.log('📝 Modal handleClose called');
    reset();
    onClose();
    console.log('📝 Modal onClose called');
  };

  return (
    <EasyCheckDialog
      open={open}
      onClose={handleClose}
      title={bank ? 'Edit Bank Account' : 'Add Bank Account'}
      variant="form"
      maxWidth="sm"
      actions={
        <>
          <EasyCheckButton
            buttonType="form"
            variant="outlined"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </EasyCheckButton>
          <EasyCheckButton
            buttonType="form"
            type="submit"
            form="bank-form"
            loading={loading}
          >
            {bank ? 'Update Account' : 'Add Account'}
          </EasyCheckButton>
        </>
      }
    >
      <form id="bank-form" onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={2} sx={{ mt: 4 }}>
          <Controller
            name="accountNumber"
            control={control}
            rules={{ 
              required: 'Account number is required',
              pattern: {
                value: /^[0-9]+$/,
                message: 'Account number should contain only numbers'
              },
              minLength: {
                value: 8,
                message: 'Account number should be at least 8 digits'
              }
            }}
            render={({ field }) => (
              <EasyCheckTextField
                {...field}
                variant="form"
                label="Bank Account Number"
                error={!!errors.accountNumber}
                helperText={errors.accountNumber?.message}
                placeholder="Enter account number"
              />
            )}
          />

          <Controller
            name="accountName"
            control={control}
            rules={{ 
              required: 'Account name is required',
              minLength: {
                value: 3,
                message: 'Account name should be at least 3 characters'
              },
              maxLength: {
                value: 100,
                message: 'Account name should not exceed 100 characters'
              }
            }}
            render={({ field }) => (
              <EasyCheckTextField
                {...field}
                variant="form"
                label="Account Name"
                error={!!errors.accountName}
                helperText={errors.accountName?.message}
                placeholder="Enter account name"
              />
            )}
          />

          <Controller
            name="currency"
            control={control}
            rules={{ required: 'Currency is required' }}
            render={({ field }) => (
              <EasyCheckSelect
                {...field}
                variant="form"
                label="Currency"
                options={currencies}
                error={!!errors.currency}
                helperText={errors.currency?.message}
              />
            )}
          />
        </Stack>
      </form>
    </EasyCheckDialog>
  );
};

export default BankFormModal; 